import { useRouteMatch } from '@react-router';
import { useCatalogRoutePaths } from '../../Catalog.routes';
import type { CatalogCategoryType } from '../../Catalog.telemetry.types';
import { tryGetStoredCurationForProduct } from '../useStoredCuration';
import type { CatalogDepartmentDictionaryEntry } from '../../state/Catalog';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { deriveProductCatalogCategoryType } from '../../utils/deriveTelemetryPage';
import { useCatalogDepartments } from '../useCatalogDepartments/useCatalogDepartments';

export type CategoryMatchingHookReturn = ReturnType<typeof useCategoryMatching>;

export const useCategoryMatching = ({ selectedCategory }: { selectedCategory: Maybe<CatalogDepartmentDictionaryEntry> }) => {
  const { getDepartmentByIdOrPath } = useCatalogDepartments();

  const { catalogRoutes } = useCatalogRoutePaths();
  const isCurationRouteMatch = useRouteMatch(catalogRoutes.curationsIndex.paths);

  const getCategoryFromUrl = useEventCallback((url: Maybe<string>, product?: Maybe<{ id: string }>) => {
    const maybeCategory = getDepartmentByIdOrPath(url);
    return [
      maybeCategory,
      deriveProductCatalogCategoryType({
        selectedCategory: maybeCategory,
        product,
        defaultValue: isCurationRouteMatch ? 'curationRoot' : 'root',
      }),
    ] as [category: CatalogDepartmentDictionaryEntry | null, categoryType: CatalogCategoryType];
  });

  const getRelevantCurationOrCategoryForProduct = useEventCallback(
    (product: Maybe<{ id: string }>, curation?: Maybe<{ id: string; publicUrlPath: string }>, selectedCategoryOverride?: Maybe<CatalogDepartmentDictionaryEntry>) => {
      // To set null or override selectedCategory
      // Page event exit and enter shows same category to fix this issue with CatalogLoggingProvider need to override selectedCategory for exit page event
      const category = selectedCategoryOverride || selectedCategoryOverride === null ? selectedCategoryOverride : selectedCategory;
      const maybeCategory = curation || tryGetStoredCurationForProduct(product) || category;
      return getCategoryFromUrl(maybeCategory?.publicUrlPath);
    },
  );

  return {
    getCategoryFromUrl,
    getRelevantCurationOrCategoryForProduct,
  };
};
